/**
 * Zod schemas for Onboarding Package — GH-237 / GH-235 / GH-245.
 *
 * Exports:
 *   createOnboardingPackageSchema        — validated create DTO (no lifecycle; defaults applied)
 *   updateOnboardingPackageSchema        — partial update DTO (lifecycle accepted; transition
 *                                          validated at service layer using canTransition)
 *   onboardingPackageResponseSchema      — full wire shape for single-package API responses
 *   packageCompletenessSummarySchema     — GH-245: compact card completeness summary
 *   packageTrustSummarySchema            — GH-245: compact card trust summary
 *   packageReviewerSummarySchema         — GH-245: compact card reviewer chip
 *   onboardingPackageListItemSchema      — GH-245: list item with optional enrichment fields
 *   validateLifecycleTransition          — pure helper: canTransition(from, to) → boolean
 *
 * Design notes:
 *   - `lifecycle` is EXCLUDED from the create schema — the server always starts at 'draft'.
 *   - `lifecycle` is INCLUDED in the update schema — but transition validity is a service
 *     concern (requires the current row's lifecycle), not a pure DTO parse concern.
 *   - `sourceSystems` stays as free-text string[]; `expectedSourceGroups` is now typed
 *     as SourceGroup[] (GH-235 P3 narrow append — enum validated at the DTO boundary).
 *   - GH-245: enrichment fields are optional on list items for backward compat.
 */

import { z } from 'zod'
import {
  CRITICALITY_VALUES,
  ONBOARDING_PACKAGE_LIFECYCLE_VALUES,
  LIFECYCLE_TRANSITIONS,
} from '../constants'
import { sourceGroupSchema } from './source-group'

// ── Base enum schemas ──────────────────────────────────────────────────────────

export const onboardingPackageLifecycleSchema = z.enum(ONBOARDING_PACKAGE_LIFECYCLE_VALUES)

export const onboardingPackageCriticalitySchema = z.enum(CRITICALITY_VALUES)

// ── Create DTO ─────────────────────────────────────────────────────────────────

/**
 * Validated shape for POST /api/onboarding-packages.
 * `lifecycle` is intentionally absent — server always initialises to 'draft'.
 */
export const createOnboardingPackageSchema = z.object({
  /** Package display name — required, 1–120 chars (whitespace-only rejected by .trim()). */
  name: z.string().trim().min(1).max(120),
  /** Plain-text purpose description — optional, max 500 chars. */
  businessPurpose: z.string().max(500).optional(),
  /** FK → users.id — optional at DTO level; service layer enforces presence from session. */
  ownerUserId: z.string().optional(),
  /** Free-text source system names — defaults to [] when omitted; max 20 entries. */
  sourceSystems: z.array(z.string().min(1).max(200)).max(20).default([]),
  /**
   * Typed expected source groups — GH-235 narrow append.
   * Values must be one of the 7 canonical SOURCE_GROUPS.
   * Defaults to [] when omitted; max 7 entries (one per group).
   */
  expectedSourceGroups: z.array(sourceGroupSchema).max(7).default([]),
  /** Business criticality — defaults to 'medium' when omitted. */
  criticality: onboardingPackageCriticalitySchema.default('medium'),
})

export type CreateOnboardingPackageDTO = z.infer<typeof createOnboardingPackageSchema>

// ── Update DTO ─────────────────────────────────────────────────────────────────

/**
 * Validated shape for PATCH /api/onboarding-packages/:id.
 * All fields optional. `lifecycle` is accepted here — transition validity is checked
 * at service layer via `validateLifecycleTransition(currentLifecycle, newLifecycle)`.
 */
export const updateOnboardingPackageSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  businessPurpose: z.string().max(500).nullable().optional(),
  ownerUserId: z.string().nullable().optional(),
  sourceSystems: z.array(z.string().min(1).max(200)).max(20).optional(),
  /**
   * Typed expected source groups — GH-235 narrow append.
   * Optional in update; when supplied, values must be canonical SOURCE_GROUPS enum members.
   */
  expectedSourceGroups: z.array(sourceGroupSchema).max(7).optional(),
  criticality: onboardingPackageCriticalitySchema.optional(),
  /** Lifecycle transition target — validated against state machine at service layer. */
  lifecycle: onboardingPackageLifecycleSchema.optional(),
  /** Required when transitioning to 'blocked'; cleared automatically on unblock.
   *  L3 fix: .trim() + .min(1) prevents whitespace-only blocked reasons. */
  blockedReason: z.string().trim().min(1).max(500).nullable().optional(),
})

export type UpdateOnboardingPackageDTO = z.infer<typeof updateOnboardingPackageSchema>

// ── Response schema ────────────────────────────────────────────────────────────

/**
 * Full wire shape returned by the API for a single onboarding package.
 * Timestamps are unix milliseconds (integer).
 */
/** Minimal hosted-site stub embedded in approved/released package detail (GH-259). */
export const packageHostedSiteSchema = z.object({
  id: z.string(),
  url: z.string(),
  /** Unix ms — createdAt of the hosted-site row. */
  publishedAt: z.number().int(),
})

export type PackageHostedSiteResponse = z.infer<typeof packageHostedSiteSchema>

export const onboardingPackageResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  businessPurpose: z.string().nullable(),
  ownerUserId: z.string().nullable(),
  sourceSystems: z.array(z.string()),
  /** GH-235: typed SourceGroup[] on responses (service always stores valid enum values). */
  expectedSourceGroups: z.array(z.string()),
  criticality: onboardingPackageCriticalitySchema,
  lifecycle: onboardingPackageLifecycleSchema,
  blockedReason: z.string().nullable(),
  /** Unix ms — row creation time. */
  createdAt: z.number().int(),
  /** Unix ms — last mutation time. */
  updatedAt: z.number().int(),
  /** Unix ms — set when lifecycle first reaches 'released'; null otherwise. */
  releasedAt: z.number().int().nullable(),
  /**
   * Linked hosted site — present on single GET when approved|released + site exists (GH-259).
   * Absent from list responses. Optional for backward compat.
   */
  hostedSite: packageHostedSiteSchema.nullable().optional(),
  /**
   * GH-268: true when this package was created via POST /sample-pack.
   * Optional + default(false) for backward compat with older cached payloads.
   */
  isTemplate: z.boolean().default(false),
})

export type OnboardingPackageResponse = z.infer<typeof onboardingPackageResponseSchema>

// ── GH-245: card enrichment schemas ───────────────────────────────────────────

/**
 * Compact completeness summary for list card rendering.
 * Score is 0-100 integer (null when no expected groups).
 */
export const packageCompletenessSummarySchema = z.object({
  score: z.number().int().min(0).max(100).nullable(),
  expectedGroups: z.number().int().min(0),
  state: z.enum(['none-expected', 'all-missing', 'partial', 'complete', 'has-failed']),
})

export type PackageCompletenessSummaryResponse = z.infer<typeof packageCompletenessSummarySchema>

/** Compact trust summary for list card rendering. */
export const packageTrustSummarySchema = z.object({
  state: z.enum(['unverified', 'pending', 'approved', 'released', 'blocked', 'safe_to_use', 'needs_review']),
  blockerCount: z.number().int().min(0),
})

export type PackageTrustSummaryResponse = z.infer<typeof packageTrustSummarySchema>

/** Compact reviewer chip for list card rendering. */
export const packageReviewerSummarySchema = z.object({
  userId: z.string(),
  displayName: z.string(),
})

export type PackageReviewerSummaryResponse = z.infer<typeof packageReviewerSummarySchema>

/**
 * Enriched list item schema — extends base response with optional card fields.
 * All enrichment fields are optional for backward compat with old cached payloads.
 */
export const onboardingPackageListItemSchema = onboardingPackageResponseSchema.extend({
  /** Assigned reviewer user ID — present in DB for joining but omitted from list item response. */
  assignedReviewerUserId: z.string().nullable().optional(),
  completeness: packageCompletenessSummarySchema.optional(),
  trust: packageTrustSummarySchema.optional(),
  reviewer: packageReviewerSummarySchema.nullable().optional(),
})

export type OnboardingPackageListItemResponse = z.infer<typeof onboardingPackageListItemSchema>

// ── Lifecycle transition helper ────────────────────────────────────────────────

/**
 * Pure function — returns true iff `to` is a valid next state from `from`.
 *
 * Same-state transitions are rejected (no no-op transitions in the state machine).
 * Used in the service layer to guard lifecycle mutation before writing to D1.
 *
 * @param from  Current lifecycle state of the package row.
 * @param to    Requested next lifecycle state from the update DTO.
 */
export function validateLifecycleTransition(
  from: (typeof ONBOARDING_PACKAGE_LIFECYCLE_VALUES)[number],
  to: (typeof ONBOARDING_PACKAGE_LIFECYCLE_VALUES)[number],
): boolean {
  if (from === to) return false
  const allowed = LIFECYCLE_TRANSITIONS[from]
  // Cast required: ReadonlyArray<...> includes() needs the element type
  return (allowed as ReadonlyArray<string>).includes(to)
}
